import { isMobile } from '@site/js/utils/breakpoint'
import { initRequest } from '@site/js/utils/request'
import { isSafari } from '@site/js/utils/utils'

import { ControlBar } from './controlbar'
import { observeDecorativeVideos, togglePlayPause } from './utils/helpers'
import { VIDEO_EMBED_CLASS, VIDEO_EMBED_POSTER_OVERLAY_CLASS, VIDEO_WRAPPER_POSTER_IMAGE_CLASS } from './utils/videoplayer-constants'

export class VideoPlayer {
  protected element: HTMLDivElement
  protected showControlBar: boolean
  protected videoPlayer: HTMLVideoElement
  private videoDetailsPage: boolean

  constructor(element: HTMLDivElement, videoDetailsPage = false) {
    this.element = element
    this.videoDetailsPage = videoDetailsPage
    this.showControlBar = this.videoDetailsPage || this.element.dataset.controls === 'true'
    this.videoPlayer = this.element.querySelector('video')
    this.init()
  }

  private init(): void {
    initRequest()
    this.isVideoEmbed() ? this.initVideoEmbed() : this.initVideo()
  }

  private initVideo(): void {
    const {
      videourl: src,
      posterimageurl: poster,
      mobileVideoDisabled,
      autoplay,
      videopreload: videoPreload,
      disableplaypausedecorativeonmobile: disablePlayPauseDecorativeOnMobile,
      playpauseonhover: playPauseOnHover,
    } = this.element.dataset

    if (!this.videoPlayer.src) {
      this.videoPlayer.src = src
    }

    if (!this.videoPlayer.poster) {
      this.videoPlayer.poster = poster
    }

    if (isSafari) {
      this.videoPlayer.style.backgroundImage = `url('${poster}')`
    }

    if (videoPreload !== 'true') {
      this.videoPlayer.autoplay = mobileVideoDisabled === 'true' && isMobile ? null : autoplay === 'true' ? true : null
      this.videoPlayer.preload = 'none'
      this.videoPlayer.controls = null
    }

    if (this.showControlBar) {
      if (poster) {
        this.renderPosterImage()
      }

      this.registerVideoPlayerEvents()
      new ControlBar(this.element, this.videoPlayer, this.getControlBarOptions())
    } else {
      observeDecorativeVideos({
        videoWrapperElement: this.element,
        videoPlayer: this.videoPlayer,
        disablePlayPauseDecorativeOnMobile,
        playPauseOnHover,
      })
    }

    document.querySelector('[video_disabled]')?.addEventListener('contextmenu', e => e.preventDefault())
  }

  private initVideoEmbed() {
    this.renderPosterImage()
  }

  private getControlBarOptions() {
    return {
      progressBar: this.element.dataset.disableProgressBar !== 'true',
      pictureInPictureToggle: this.element.dataset.disablePictureInPictureToggle !== 'true',
      currentTimeDisplay: this.element.dataset.disableCurrentTimeDisplay !== 'true',
      remainingTimeDisplay: this.element.dataset.disableRemainingTimeDisplay !== 'true',
      timeDivider: this.element.dataset.disableTimeDivider !== 'true',
      fullScreenDisplay: this.element.dataset.disableFullScreenDisplay !== 'true',
      volumeDisplay: this.element.dataset.disableVolumeDisplay !== 'true',
      seekButtons: this.element.dataset.disableSeekButtons !== 'true',
      playAndPauseButton: this.element.dataset.disablePlayAndPauseButton !== 'true',
    }
  }

  protected registerVideoPlayerEvents() {
    this.videoPlayer.addEventListener('click', () => {
      togglePlayPause(this.videoPlayer, this.element)
    })
  }

  private renderPosterImage() {
    const backgroundImage = `url(${this.element.dataset.posterimageurl})`

    if (this.isVideoEmbed()) {
      const videoEmbedPosterImageDiv: HTMLDivElement = this.element.querySelector(`.${VIDEO_EMBED_POSTER_OVERLAY_CLASS}`)
      if (videoEmbedPosterImageDiv) {
        videoEmbedPosterImageDiv.style.backgroundImage = backgroundImage
      }
      return
    }

    const posterDiv = document.createElement('div')
    posterDiv.classList.add(VIDEO_WRAPPER_POSTER_IMAGE_CLASS)
    posterDiv.style.backgroundImage = backgroundImage
    posterDiv.addEventListener('click', () => togglePlayPause(this.videoPlayer, this.element))
    this.element.appendChild(posterDiv)
  }

  private isVideoEmbed() {
    return this.element.classList.contains(VIDEO_EMBED_CLASS)
  }
}
